import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  OnChanges,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
  SimpleChanges,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators
} from '@angular/forms';

@Component({
  selector: 'password-field',
  host: {
    class: 'password-field',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-required]': 'isRequired().toString()',
    '[attr.aria-disabled]': 'isDisabled().toString()',
    '[attr.aria-readonly]': 'readonly().toString()',
    '[attr.aria-invalid]': 'isInvalid().toString()',
    '[class.input-disabled]': 'isDisabled()',
    '[class.input-readonly]': 'readonly()'
  },
  standalone: true,
  template: `<div class="password-field__container">
    <input
      [type]="isHidden() ? 'password' : 'text'"
      [id]="id()"
      class="password-field-input input-field"
      (focus)="handleInputFocus()"
      (input)="handleInput($event)"
      (blur)="handleBlur()"
      [attr.disabled]="isDisabled() || null"
      [attr.readonly]="readonly() || null"
      [attr.placeholder]="placeholder() || null"
      [attr.value]="value() || ''"
    />

    @if (showVisibilityButton()) {
      <button
        type="button"
        class="password-field-visibility-button"
        (click)="toggleVisibility()"
      >
        @if (isHidden()) {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
            <defs>
              <style>
                .fa-secondary {
                  opacity: 0.4;
                }
              </style>
            </defs>
            <path
              class="fa-primary"
              d="M224 256C259.3 256 288 227.3 288 192C288 180.5 284.1 169.7 279.6 160.4C282.4 160.1 285.2 160 288 160C341 160 384 202.1 384 256C384 309 341 352 288 352C234.1 352 192 309 192 256C192 253.2 192.1 250.4 192.4 247.6C201.7 252.1 212.5 256 224 256z"
            />
            <path
              class="fa-secondary"
              d="M95.42 112.6C142.5 68.84 207.2 32 288 32C368.8 32 433.5 68.84 480.6 112.6C527.4 156 558.7 207.1 573.5 243.7C576.8 251.6 576.8 260.4 573.5 268.3C558.7 304 527.4 355.1 480.6 399.4C433.5 443.2 368.8 480 288 480C207.2 480 142.5 443.2 95.42 399.4C48.62 355.1 17.34 304 2.461 268.3C-.8205 260.4-.8205 251.6 2.461 243.7C17.34 207.1 48.62 156 95.42 112.6V112.6zM288 400C367.5 400 432 335.5 432 256C432 176.5 367.5 112 288 112C208.5 112 144 176.5 144 256C144 335.5 208.5 400 288 400z"
            />
          </svg>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
            <defs>
              <style>
                .fa-secondary {
                  opacity: 0.4;
                }
              </style>
            </defs>
            <path
              class="fa-primary"
              d="M5.112 9.196C13.29-1.236 28.37-3.065 38.81 5.112L630.8 469.1C641.2 477.3 643.1 492.4 634.9 502.8C626.7 513.2 611.6 515.1 601.2 506.9L9.196 42.89C-1.236 34.71-3.065 19.63 5.112 9.196V9.196z"
            />
            <path
              class="fa-secondary"
              d="M446.6 324.7C457.7 304.3 464 280.9 464 256C464 176.5 399.5 112 320 112C282.7 112 248.6 126.2 223.1 149.5L150.7 92.77C195 58.27 251.8 32 320 32C400.8 32 465.5 68.84 512.6 112.6C559.4 156 590.7 207.1 605.5 243.7C608.8 251.6 608.8 260.4 605.5 268.3C592.1 300.6 565.2 346.1 525.6 386.7L446.6 324.7zM313.4 220.3C317.6 211.8 320 202.2 320 192C320 180.5 316.1 169.7 311.6 160.4C314.4 160.1 317.2 160 320 160C373 160 416 202.1 416 256C416 269.7 413.1 282.7 407.1 294.5L313.4 220.3zM320 480C239.2 480 174.5 443.2 127.4 399.4C80.62 355.1 49.34 304 34.46 268.3C31.18 260.4 31.18 251.6 34.46 243.7C44 220.8 60.29 191.2 83.09 161.5L177.4 235.8C176.5 242.4 176 249.1 176 256C176 335.5 240.5 400 320 400C338.7 400 356.6 396.4 373 389.9L446.2 447.5C409.9 467.1 367.8 480 320 480H320z"
            />
          </svg>
        }
      </button>
    }
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true
    }
  ]
})
export class PasswordComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  private _platformId = inject(PLATFORM_ID);

  valueChanged = output<string>();

  value = input<string | null | undefined>();
  placeholder = input<string | undefined | null>();
  uniqueId = input.required<string>();
  ariaLabel = input<string | undefined | null>();
  disabled = input(false);
  readonly = input(false);
  showVisibilityButton = input(true);

  id = signal('');
  isInvalid = signal(false);
  isDisabled = signal(false);
  isRequired = signal(false);
  isHidden = signal(true);

  onChange: (value: unknown) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _injector: Injector,
    private _renderer: Renderer2
  ) {}

  get host(): HTMLElement {
    return this._viewContainerRef.element.nativeElement as HTMLElement;
  }

  get textField(): HTMLInputElement {
    return this.host.querySelector('.password-field-input') as HTMLInputElement;
  }

  get visibilityButton(): HTMLButtonElement | null {
    return this.host.querySelector(
      '.password-field-visibility-button'
    ) as HTMLButtonElement | null;
  }

  private get hostFormField(): HTMLElement | null {
    return this.host.closest('.form-field');
  }

  private get inputEvent(): InputEvent {
    const event = new InputEvent('input', { cancelable: true, bubbles: false });
    return event;
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  private get _ngControl(): NgControl | null {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });

    return ngControl;
  }

  ngOnInit(): void {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
      this.isRequired.set(
        this._ngControl.control?.hasValidator(Validators.required) ?? false
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this.id.set(this.uniqueId());
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.hostFormField) {
      throw new Error(
        'To ensure proper functionality, InputSearchComponent should be hosted within a FormFieldComponent.'
      );
    }
  }

  writeValue(value: string | null | undefined): void {
    this.textField.value = value || '';
    value
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    isDisabled
      ? this._addClassToHostElement('input-disabled')
      : this._removeClassFromHostElement('input-disabled');
  }

  clearInput(): void {
    this.textField.value = '';
    this.textField.dispatchEvent(this.inputEvent);
    this._removeClassFromHostElement('input-filled');
    this.textField.focus();
  }

  focusInput(): void {
    this.textField.focus();
  }

  protected handleInputFocus(): void {
    this._addClassToHostElement('input-focused');
  }

  protected handleBlur(): void {
    this.onTouched();
    this._removeClassFromHostElement('input-focused');
  }

  protected handleInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onChange(value);
    this.valueChanged.emit(value);
    value
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');
  }

  protected toggleVisibility(): void {
    this.isHidden.set(!this.isHidden());
  }

  private _removeClassFromHostElement(value: string): void {
    this._renderer.removeClass(this.host, value);
  }

  private _addClassToHostElement(value: string): void {
    this._renderer.addClass(this.host, value);
  }
}
